import { IconX } from '@tabler/icons-react';
import {
    PropsWithChildren,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import Button from 'shared/Button';
import withClassNames from 'utils/hoc/withClassNames';
import {
    hideZendeskWidget,
    showZendeskWidget,
} from 'utils/hooks/useZendeskWidget';
import DoubleBadge from '../DoubleBadge';
import { isEmpty } from 'lodash';
import classNames from 'classnames';
import { DrawerFooter } from './DrawerFooter';

interface DrawerProps extends PropsWithChildren {
    closeText?: string;
    content?: ReactNode;
    header?: {
        title?: string;
        description?: string | ReactNode;
        buttons?: ReactNode[];
        badge?: ReactNode;
        content?: ReactNode;
    };
    onClose: () => void;
    title: ReactNode;
    titleBadge?: {
        text?: string;
        id?: string | number;
    };
    type?: 'default' | 'narrow';
    visible?: boolean;
    footerButtons?: ReactNode[];
}

const DrawerOpen = withClassNames(
    'div',
    'fixed top-0 right-0 h-screen w-screen z-(--z-drawer)'
);

const DrawerTitle = withClassNames(
    'h1',
    'flex-1 m-0 text-base font-semibold truncate flex-auto min-w-8'
);

const TRANSITION_DURATION_MS = 150;

export const Drawer = ({
    visible,
    title,
    children,
    onClose,
    closeText = 'Close',
    header,
    titleBadge,
    type = 'default',
    footerButtons,
}: DrawerProps) => {
    const isControlled = visible !== undefined;
    const [internalOpen, setInternalOpen] = useState(true);
    const effectiveVisible = isControlled ? visible! : internalOpen;

    const isTypeDefault = useMemo(() => type === 'default', [type]);
    const [shouldRender, setShouldRender] = useState(effectiveVisible);
    const [moveDrawer, setMoveDrawer] = useState(false);

    useEffect(() => {
        document.body.classList.toggle('overflow-hidden', effectiveVisible);
        effectiveVisible ? hideZendeskWidget() : showZendeskWidget();
    }, [effectiveVisible]);

    /*
     * When drawer is simply unmounted in parent component and without setting
     * `visible` prop to `false`, make sure document scroll lock is removed in
     * such case as well.
     */
    useEffect(() => {
        return () => {
            showZendeskWidget();
            document.body.classList.remove('overflow-hidden');
        };
    }, []);

    useEffect(() => {
        if (effectiveVisible) {
            setShouldRender(true);
            const timer = setTimeout(() => setMoveDrawer(true), 10);
            return () => clearTimeout(timer);
        } else {
            setMoveDrawer(false);
            const timer = setTimeout(
                () => setShouldRender(false),
                TRANSITION_DURATION_MS
            );
            return () => clearTimeout(timer);
        }
    }, [effectiveVisible]);

    const handleClose = useCallback(() => {
        if (!isControlled) {
            setInternalOpen(false);
        }
        onClose?.();
    }, [isControlled, onClose]);

    if (!shouldRender) {
        return <></>;
    }

    return (
        <DrawerOpen
            tabIndex={-1}
            data-testid="Drawer"
            onClick={(event) => {
                const isClickOutside = event.currentTarget === event.target;

                if (isClickOutside) {
                    handleClose();
                }
            }}
            onKeyDown={({ key }) => {
                key === 'Escape' ? handleClose() : null;
            }}
        >
            <aside
                className={classNames(
                    'transition-transform flex box-border flex-col absolute right-0 h-full bg-white border border-gray-200 shadow-2xl w-96',
                    isTypeDefault
                        ? 'drawer-md:w-[512px] drawer-lg:w-[768px]'
                        : 'drawer-md:w-[448px]',
                    moveDrawer ? 'translate-x-0' : 'translate-x-full'
                )}
            >
                <div className="p-4 drawer-lg:px-6 drawer-lg:py-5 bg-gray-50 border-gray-200 border-b flex justify-between items-center">
                    <div
                        className={classNames(
                            'flex justify-center gap-2 flex-col',
                            {
                                ['max-w-[calc(100%_-_48px)] drawer-lg:flex-row']:
                                    isTypeDefault,
                            }
                        )}
                    >
                        <DrawerTitle>{title}</DrawerTitle>
                        {!isEmpty(titleBadge) && titleBadge.text && (
                            <div
                                data-testid="title-badge"
                                className="rounded-sm p-[3px] gap-3 bg-gray-200 flex items-center flex-auto min-w-8"
                            >
                                <span
                                    data-testid="drawer-badgetext"
                                    className="leading-none text-base font-normal truncate"
                                >
                                    {titleBadge.text}
                                </span>
                                {titleBadge.id && (
                                    <DoubleBadge>{titleBadge.id}</DoubleBadge>
                                )}
                            </div>
                        )}
                    </div>
                    <Button
                        autoFocus
                        className="drawer-close-button border-none p-1.5 [&:not(:focus-visible):not(:hover)]:text-disabled"
                        color="light"
                        size="iconMd"
                        title={closeText}
                        onClick={handleClose}
                    >
                        <IconX />
                    </Button>
                </div>
                {!isEmpty(header) && (
                    <div className="p-4 drawer-lg:p-6">
                        <div className="flex gap-2 flex-col drawer-lg:flex-row">
                            <div className="flex flex-col gap-1 flex-1 min-w-0">
                                <div
                                    data-testid="header-badge-title"
                                    className="flex flex-wrap gap-2"
                                >
                                    <h2
                                        className="lg:text-2xl text-lg font-semibold m-0 p-0 truncate"
                                        title={header.title}
                                    >
                                        {header.title}
                                    </h2>
                                    {header.badge}
                                </div>
                                {header.description && (
                                    <span
                                        data-testid="header-description"
                                        className="text-sm font-normal text-secondary"
                                    >
                                        {header.description}
                                    </span>
                                )}
                            </div>
                            {!isEmpty(header.buttons) && (
                                <div className="flex gap-2 justify-center">
                                    {header.buttons?.map((button, index) => (
                                        <div key={index}>{button}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {header.content && (
                            <div className="flex justify-center mt-4">
                                {header.content}
                            </div>
                        )}
                    </div>
                )}
                {children}
                {!isEmpty(footerButtons) && (
                    <DrawerFooter>{footerButtons}</DrawerFooter>
                )}
            </aside>
        </DrawerOpen>
    );
};
